import React, { useState, useEffect } from 'react';
import { GraduationCap, User, Calendar, DollarSign, RefreshCw, Download, BookOpen, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminEnrollments = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const { token } = useAuth();

  const fetchEnrollments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/enrollments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrollments(response.data);
    } catch (error) {
      toast.error('Erro ao carregar matrículas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const handleDelete = async (enrollmentId, userName, itemTitle) => {
    if (!window.confirm(`Tem certeza que deseja excluir a matrícula de "${userName}" no curso "${itemTitle}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    setDeleting(enrollmentId);
    try {
      await axios.delete(`${API}/admin/enrollments/${enrollmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Matrícula excluída com sucesso!');
      fetchEnrollments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir matrícula');
    } finally {
      setDeleting(null);
    }
  };

  const exportToCSV = () => {
    if (enrollments.length === 0) {
      toast.error('Nenhuma matrícula para exportar');
      return;
    }

    const headers = ['Aluno', 'Email', 'Curso/Formação', 'Modalidade', 'Valor', 'Status', 'Data Matrícula'];
    const csvContent = [
      headers.join(','),
      ...enrollments.map(e => [
        `"${e.user_name}"`,
        `"${e.user_email}"`,
        `"${e.item_title}"`,
        `"${e.modality}"`,
        `"R$ ${e.price_paid?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}"`,
        `"${e.status}"`,
        `"${new Date(e.enrolled_at).toLocaleDateString('pt-BR')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `matriculas_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Matrículas exportadas com sucesso!');
  };

  const getModalityBadge = (modality) => {
    const config = {
      ead_recorded: { label: 'EaD Gravado', className: 'bg-blue-600' },
      ead_live: { label: 'Elite', className: 'bg-purple-600' },
      presential: { label: 'Mentor', className: 'bg-red-600' }
    };
    const m = config[modality] || { label: modality, className: 'bg-gray-600' };
    return <Badge className={m.className}>{m.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: 'Ativo', className: 'bg-green-600' },
      pending: { label: 'Pendente', className: 'bg-yellow-600' },
      completed: { label: 'Concluído', className: 'bg-blue-600' },
      cancelled: { label: 'Cancelado', className: 'bg-red-600' }
    };
    const s = config[status] || { label: status, className: 'bg-gray-600' };
    return <Badge className={s.className}>{s.label}</Badge>;
  };

  // Calculate totals
  const totalRevenue = enrollments.reduce((sum, e) => sum + (e.price_paid || 0), 0);
  const activeCount = enrollments.filter(e => e.status === 'active').length;

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="pt-6 text-center text-gray-400">
          Carregando matrículas...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Matrículas</p>
                <p className="text-2xl font-bold text-white">{enrollments.length}</p>
              </div>
              <GraduationCap className="text-red-500" size={32} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Alunos Ativos</p>
                <p className="text-2xl font-bold text-white">{activeCount}</p>
              </div>
              <User className="text-green-500" size={32} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Receita Total</p>
                <p className="text-2xl font-bold text-white">
                  R$ {totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <DollarSign className="text-yellow-500" size={32} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enrollments Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white">
            Matrículas Realizadas
          </CardTitle>
          <div className="flex gap-2">
            <Button
              onClick={fetchEnrollments}
              variant="outline"
              size="sm"
              className="border-zinc-700 text-white"
            >
              <RefreshCw className="mr-2" size={16} />
              Atualizar
            </Button>
            <Button
              onClick={exportToCSV}
              variant="outline"
              size="sm"
              className="border-zinc-700 text-white"
            >
              <Download className="mr-2" size={16} />
              Exportar CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {enrollments.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <BookOpen size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nenhuma matrícula realizada ainda.</p>
              <p className="text-sm">As matrículas aparecerão aqui quando alunos se matricularem nos cursos.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Aluno</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Curso/Formação</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Modalidade</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Valor</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Data</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollments.map((enrollment) => (
                    <tr key={enrollment.id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="text-white">{enrollment.user_name}</p>
                          <p className="text-gray-500 text-sm">{enrollment.user_email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-white">
                        {enrollment.item_title}
                      </td>
                      <td className="py-3 px-4">
                        {getModalityBadge(enrollment.modality)}
                      </td>
                      <td className="py-3 px-4 text-green-400 font-semibold">
                        R$ {enrollment.price_paid?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(enrollment.status)}
                      </td>
                      <td className="py-3 px-4 text-gray-400 text-sm">
                        {new Date(enrollment.enrolled_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          onClick={() => handleDelete(enrollment.id, enrollment.user_name, enrollment.item_title)}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                          disabled={deleting === enrollment.id}
                          title="Excluir matrícula"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEnrollments;
